import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { Form, Formik } from 'formik';
import FormikPhoneNumberInput from 'src/shared-components/Input/PhoneNumberInput/FormikPhoneNumberInput';
import FormField from 'src/shared-components/FormComponents/FormField';
import {
  isPhoneCodeValid,
  isPhoneNumberValid
} from 'src/shared-components/Input/PhoneNumberInput/PhoneNumberInput';
import ButtonBase, {
  ButtonColor,
  ButtonSize
} from 'src/shared-components/Button';
import useNotification, { NotificationType } from 'src/hooks/useNotification';
import trnsCommon from 'src/hooks/trns';
import postNoSearchResultFeedback from 'src/api/requests/postNoSearchResultFeedback';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import { DrugItem } from 'src/api/types';
import trns from './trns';

interface ISubmitPayload {
  phone: string;
  name: string;
  searchValue: string;
}

interface IOnSubmitForm {
  searchValue: string;
  drugList?: DrugItem[] | null;
  onSubmitForm?: (values: ISubmitPayload) => void;
  gtagPrefix?: GTAGPrefix;
}

const NoSearchResultForm: FC<IOnSubmitForm> = ({
  searchValue,
  gtagPrefix,
  onSubmitForm = () => null
}) => {
  const [isSubmitSucceed, setIsSubmitSucceed] = useState(false);
  const intl = useIntl();
  const router = useRouter();
  const { showNotification } = useNotification();
  const { trackGtagEvent } = useGTAG();

  const validateFormik = (values) => {
    const errors = {
      phone: null
    };

    // phone validation
    if (!values?.phone) {
      errors.phone = <FormattedMessage {...trns.requiredFieldLabel} />;
    } else if (!isPhoneNumberValid(values.phone)) {
      errors.phone = <FormattedMessage {...trns.incorrectPhoneNumberLabel} />;
    } else if (!isPhoneCodeValid(values.phone)) {
      errors.phone = <FormattedMessage {...trns.incorrectPhoneCode} />;
    }

    if (!errors.phone) {
      return null;
    }
    return errors;
  };

  useEffect(() => {
    if (isSubmitSucceed) {
      trackGtagEvent(
        `${gtagPrefix}_empty_request_success`,
        searchValue,
        'search_request'
      );
    }
  }, [isSubmitSucceed]);

  const onSubmit = async (values) => {
    try {
      await postNoSearchResultFeedback({ ...values, searchValue });
      showNotification({
        message: (
          <>
            <p className="fs-body-bold">
              {intl.formatMessage(trns.notificationTitle)}
            </p>
            <p className="fs-body-reg">
              {intl.formatMessage(trns.notificationMessage)}
            </p>
          </>
        ),
        type: NotificationType.Notification
      });
      onSubmitForm({ ...values, searchValue });
      router.push('/');
    } catch (error) {
      showNotification({
        message:
          error?.message || intl.formatMessage(trnsCommon.unExpectedError),
        type: NotificationType.Error
      });
    }
  };

  return (
    <div className="max-w-2xl px-8 py-6 bg-wildSand text-left">
      <p className="fs-body-lg-bold">
        <FormattedMessage {...trns.noSearchResultHeader} />
      </p>
      <p className="fs-body-reg my-6">
        <FormattedMessage {...trns.noSearchResultMessage} />
      </p>
      <Formik
        initialValues={{
          phone: null,
          name: null
        }}
        onSubmit={(values) => {
          onSubmit(values);
          setIsSubmitSucceed(true);
        }}
        validate={validateFormik}
      >
        {({ handleSubmit }) => (
          <Form>
            <div className="flex flex-col space-y-5 mb-5 max-w-xs">
              <FormikPhoneNumberInput name="phone" required />
              <FormField
                name="name"
                placeholder={intl.formatMessage(
                  trns.noSearchResultFormNameFieldPlaceholder
                )}
              />
              <ButtonBase
                color={ButtonColor.vermilion}
                size={ButtonSize.md}
                label={<FormattedMessage {...trns.noSearchResultFormSubmit} />}
                onClick={() => {
                  handleSubmit();
                  setIsSubmitSucceed(false);
                  trackGtagEvent(
                    `${gtagPrefix}_empty_request_send`,
                    searchValue,
                    'search_request'
                  );
                }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NoSearchResultForm;
